import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { Card } from "@/components/Card";
import { CurrencyHashrateChart } from "@/components/CurrencyHashrateChart";
import { StatCard } from "@/components/StatCard";
import { formatHash } from "@/utils/to-hash";
import { getCurrencyIncome } from "@/store/sub_accounts/api";
import { CurrencyGroup } from "@/hooks/use-subaccounts-by-currency";
import style from "./index.module.scss";

interface IProps {
  group: CurrencyGroup;
  // The group holds only the ticked sub-accounts, not the whole currency.
  selected?: boolean;
}

// Screen 2 ("<Currency> / Все <Currency>"): sums are computed from
// summary_data already embedded per row by GET .../all/
// (SubAccountDetailSerializer). The latest-day income per sub-account comes
// from one request for the whole currency (GET .../currency_income/), not
// one earnings request per sub-account.
export function CurrencyAggregate({ group, selected = false }: IProps) {
  const i18n = useTranslations("cabinet.statistics");
  const [todayIncome, setTodayIncome] = useState<Record<string, number>>({});

  useEffect(() => {
    const controller = new AbortController();
    getCurrencyIncome(group.currency, { signal: controller.signal })
      .then((response) => {
        const rows = response.data as Record<string, string>;
        setTodayIncome(
          Object.fromEntries(
            Object.entries(rows).map(([name, value]) => [name, Number(value)]),
          ),
        );
      })
      .catch(() => setTodayIncome({}));
    return () => controller.abort();
  }, [group.currency, group.subAccounts.length]);

  const totals = group.subAccounts.reduce(
    (acc, s) => {
      const summary = s.summary_data || {};
      acc.hashrate10m += Number(summary.hashrate_10min || 0);
      acc.hashrate24h += Number(summary.hashrate_24hour || 0);
      acc.balance += Number(summary.balance || 0);
      acc.online += Number(summary.active_workers || 0);
      acc.offline += Number(summary.unactive_workers || 0);
      return acc;
    },
    { hashrate10m: 0, hashrate24h: 0, balance: 0, online: 0, offline: 0 },
  );
  // "Последний доход" (mockup Screen 2) - sum of the same per-sub-account
  // latest-day figures already fetched above for the table's income column.
  const lastIncome = group.subAccounts.reduce(
    (sum, s) => sum + (todayIncome[s.name] ?? 0),
    0,
  );

  return (
    <div>
      <div className={style.kpiGrid}>
        <Card>
          <StatCard
            color="blue"
            icon="fa-dollar-sign"
            label={i18n("aggregateTable.lastIncome")}
            value={`${lastIncome.toFixed(8)} ${group.currency}`}
          />
        </Card>
        <Card>
          <StatCard
            color="teal"
            icon="fa-bolt"
            label={i18n("hashrate10min")}
            value={formatHash(totals.hashrate10m, group.currency)}
          />
        </Card>
        <Card>
          <StatCard
            color="purple"
            icon="fa-chart-area"
            label={i18n("hashrate24h")}
            value={formatHash(totals.hashrate24h, group.currency)}
          />
        </Card>
        <Card>
          <StatCard
            color="green"
            icon="fa-users"
            label={i18n("worker")}
            value={String(totals.online)}
          />
        </Card>
        <Card>
          <StatCard
            color="orange"
            icon="fa-user-slash"
            label={i18n("inactive_workers")}
            value={String(totals.offline)}
          />
        </Card>
        <Card>
          <StatCard
            color="blue"
            icon="fa-wallet"
            label={i18n("balance")}
            value={`${totals.balance.toFixed(8)} ${group.currency}`}
          />
        </Card>
      </div>
      <CurrencyHashrateChart
        currency={group.currency}
        subAccountNames={
          selected ? group.subAccounts.map((s) => s.name) : undefined
        }
      />
      <Card className={style.tableCard}>
        <div className="generic-table">
          <table className="table">
            <thead>
              <tr>
                <th>{i18n("aggregateTable.name")}</th>
                <th>{i18n("aggregateTable.hashrateNow")}</th>
                <th>{i18n("aggregateTable.hashrate24h")}</th>
                <th>{i18n("aggregateTable.online")}</th>
                <th>{i18n("aggregateTable.offline")}</th>
                <th>{i18n("aggregateTable.balance")}</th>
                <th>{i18n("aggregateTable.income24h")}</th>
              </tr>
            </thead>
            <tbody>
              {group.subAccounts.map((subAccount) => {
                const summary = subAccount.summary_data || {};
                return (
                  <tr key={subAccount.id}>
                    <td>{subAccount.name}</td>
                    <td>{formatHash(Number(summary.hashrate_10min || 0), group.currency)}</td>
                    <td>{formatHash(Number(summary.hashrate_24hour || 0), group.currency)}</td>
                    <td>{summary.active_workers ?? 0}</td>
                    <td>{summary.unactive_workers ?? 0}</td>
                    <td>
                      {Number(summary.balance || 0).toFixed(8)} {group.currency}
                    </td>
                    <td>
                      {(todayIncome[subAccount.name] ?? 0).toFixed(8)}{" "}
                      {group.currency}
                    </td>
                  </tr>
                );
              })}
              {!group.subAccounts.length && (
                <tr>
                  <td colSpan={7} className="text-center">
                    —
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
